import { useCallback, useEffect, useState } from "react";
import { Lock, Trash2, Target, AlertTriangle, Lightbulb, HelpCircle, StickyNote, Flag } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { EmptyState } from "@/components/Mascot";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CATS = {
  struggle: { label: "Topics you struggle with", icon: AlertTriangle, cls: "text-yellow-200" },
  mistake: { label: "Mistakes you tend to make", icon: Flag, cls: "text-red-300" },
  learning_style: { label: "What helps you learn", icon: Lightbulb, cls: "text-emerald-300" },
  goal: { label: "Your goals", icon: Target, cls: "text-sky-300" },
  question: { label: "Questions you've asked", icon: HelpCircle, cls: "text-violet-300" },
  note: { label: "Other notes", icon: StickyNote, cls: "text-neutral-300" },
};

export default function PrivateMemoryPage() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => http.get("/memory/private").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try {
      await http.delete(`/memory/private/${id}`);
      setItems((x) => x.filter((i) => i.entry_id !== id));
    } catch (e) { toast.error(formatError(e)); }
  };
  const clearAll = async () => {
    try {
      await http.delete("/memory/private");
      setItems([]);
      toast.success("Your private memory was cleared");
    } catch (e) { toast.error(formatError(e)); }
  };

  const groups = Object.keys(CATS).map((k) => ({ key: k, ...CATS[k], items: (items || []).filter((i) => i.category === k) })).filter((g) => g.items.length);

  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="inline-flex items-center gap-2 text-xs text-emerald-300 border border-emerald-300/30 bg-emerald-500/10 rounded-full px-3 py-1 mb-4">
              <Lock className="h-3 w-3" /> Private · only you and your tutor
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">What I know about you</h1>
            <p className="text-neutral-400 mt-3 max-w-xl">Notes I keep to help you specifically. They're never shared with other students. Delete anything you like.</p>
          </div>
          {items?.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="clear-private-memory-btn" className="text-sm px-4 py-2 rounded-full border border-red-400/30 text-red-300 hover:bg-red-500/10 transition-colors">Clear all</button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0E121C] border-white/10">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear your private memory?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes everything BrynAI knows about you specifically.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="clear-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAll} className="bg-red-500 hover:bg-red-400" data-testid="clear-confirm-btn">Clear all</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {items && !items.length && (
          <EmptyState testid="private-memory-empty" pose="thinking" title="Nothing here yet" text="As we study together, I'll note what helps you here." action="Start a chat" to="/app" />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="private-memory-grid">
          {groups.map((g) => (
            <div key={g.key} className="rounded-2xl border border-white/10 bg-[#121725] p-6 rise">
              <div className={`flex items-center gap-2 mb-4 ${g.cls}`}>
                <g.icon className="h-4 w-4" />
                <h3 className="font-display font-medium text-base">{g.label}</h3>
                <span className="ml-auto text-xs text-neutral-500">{g.items.length}</span>
              </div>
              <ul className="space-y-2">
                {g.items.map((i) => (
                  <li key={i.entry_id} className="group flex gap-2 text-sm text-neutral-300 leading-relaxed" data-testid={`private-memory-item-${i.entry_id}`}>
                    <span className="flex-1">{i.content}</span>
                    <button onClick={() => del(i.entry_id)} data-testid={`private-memory-delete-${i.entry_id}`}
                      className="opacity-40 group-hover:opacity-100 text-neutral-500 hover:text-red-400 transition-opacity h-fit mt-0.5">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
